package io.horion.cv.components.preview

import androidx.compose.runtime.Composable
import io.horion.cv.api.datasource.cv.OrionCV
import io.horion.cv.utils.Colors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun CvThreePreview(data: OrionCV) {

    val pi = data.personalInformation
    val validPersonalizedSections =
        data.personalizedSections.filter { section ->
            section.resume.isNotBlank() ||
                    section.started.isNotBlank() ||
                    section.ended.isNotBlank() ||
                    section.description.isNotBlank()
        }
    val grayText = Color("#333333")
    val softGrayBg = Color("#F1F1F1")

    val filteredCompetences = data.competences.filter { it.name.isNotBlank() }
    val filteredLanguages   = data.languages.filter { it.language.name.isNotBlank() }
    val filteredQualities   = data.qualities.filter { it.isNotBlank() }
    val filteredSoftware    = data.software.filter { it.name.isNotBlank() }
    val filteredHobbies     = data.hobbies.filter { it.isNotBlank() }

    val filteredFormations = data.formations.filter {
        it.label.isNotBlank() || it.fieldOfStudy?.isNotBlank() == true || it.school.isNotBlank() || it.description.isNotBlank()
    }
    val filteredExperiences = data.experiences.filter {
        it.post.isNotBlank() || it.enterprise.isNotBlank() || it.description.any { desc -> desc.isNotBlank() }
    }
    val filteredStages = data.stages.filter {
        it.post.isNotBlank() || it.enterprise.isNotBlank() || it.description.isNotBlank()
    }
    val filteredLearns = data.learns.filter {
        it.label.isNotBlank() || it.description.isNotBlank()
    }
    val filteredActivities = data.extracurricularActivities.filter {
        it.name.isNotBlank() || it.enterprise.isNotBlank() || it.description.isNotBlank()
    }
    val filteredCertificates = data.certificates.filter {
        it.name.isNotBlank() || it.description.isNotBlank()
    }
    val filteredAchievements = data.achievements.filter { it.isNotBlank() }
    val filteredReferences   = data.references.filter { it.name.isNotBlank() }

    Style {
        """
    .cv-three-preview,
    .cv-three-preview * {
        box-sizing: border-box;
    }

    .cv-three-preview div,
    .cv-three-preview span,
    .cv-three-preview a,
    .cv-three-preview p,
    .cv-three-preview li,
    .cv-three-preview h1,
    .cv-three-preview h2,
    .cv-three-preview h3 {
        max-width: 100%;
        min-width: 0;
        white-space: normal;
        overflow-wrap: anywhere;
        word-break: break-word;
    }

    .cv-three-preview img {
        max-width: 100%;
        height: auto;
    }
    """.trimIndent()
    }

    Div(attrs = {
        attr("class", "cv-three-preview")
        style {
            display(DisplayStyle.Flex)
            width(794.px)
            property("max-width", "100%")
            property("min-width", "0")
            minHeight(1123.px)
            fontFamily("Inter", "Ubuntu", "sans-serif")
            backgroundColor(Colors.White)
            property("box-sizing", "border-box")
            property("overflow", "hidden")
        }
    }) {
        // --- SIDEBAR GAUCHE ---
        Div(attrs = {
            style {
                width(270.px)
                width(270.px)
                property("min-width", "270px")
                property("max-width", "270px")
                backgroundColor(softGrayBg)
                padding(30.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                property("flex-shrink", "0")
                property("overflow", "hidden")
                boxSizing("border-box")
            }
        }) {
            // Photo
            if (pi.photo.isNotBlank()) {
                Div(attrs = { style {
                    width(120.px)
                    height(120.px)
                    borderRadius(50.percent)
                    overflow("hidden")
                    margin(0.px, 0.px, 20.px)
                    property("margin-left", "auto")
                    property("margin-right", "auto")
                }}) {
                    Img(src = pi.photo, attrs = { style {
                        width(100.percent)
                        height(100.percent)
                        property("object-fit", "cover")
                    }})
                }
            }

            // CONTACT
            val hasWebsite = pi.siteWeb?.second?.isNotBlank() == true
            val hasLinks   = pi.links.any { it.second.isNotBlank() }
            val location = listOfNotNull(
                pi.city.takeIf { it.isNotBlank() },
                pi.state.takeIf { it.isNotBlank() },
                pi.country.takeIf { it.isNotBlank() }
            ).joinToString(" ")

            if (pi.phone.isNotBlank() || pi.email.isNotBlank() || location.isNotBlank() || hasWebsite || hasLinks) {
                SimpleBlackSectionTitle("CONTACT")
                if (pi.phone.isNotBlank()) SimpleCv3ContactRow(pi.phone)
                if (pi.email.isNotBlank()) SimpleCv3ContactRow(pi.email)
                if (location.isNotBlank()) SimpleCv3ContactRow(location)
                pi.siteWeb?.let { (_, url) -> if (url.isNotBlank()) SimpleCv3ContactLinkRow(url) }
                pi.links.forEach { (_, url) -> if (url.isNotBlank()) SimpleCv3ContactLinkRow(url) }
            }

            // PROFILE
            if (pi.lastName.isNotBlank() || pi.firstName.isNotBlank() || pi.birth.isNotBlank() || pi.nationality.isNotBlank() || pi.maritalStatus.isNotBlank()) {
                SimpleBlackSectionTitle("PROFILE")
                if (pi.lastName.isNotBlank())      SimpleCv3DetailRow("Nom", pi.lastName)
                if (pi.firstName.isNotBlank())     SimpleCv3DetailRow("Prénom", pi.firstName)
                if (pi.birth.isNotBlank())         SimpleCv3DetailRow("Née le", pi.birth)
                if (pi.nationality.isNotBlank())   SimpleCv3DetailRow("Nat.", pi.nationality)
                if (pi.maritalStatus.isNotBlank()) SimpleCv3DetailRow("Sit. fam.", pi.maritalStatus)
            }

            // EDUCATION
            if (filteredFormations.isNotEmpty()) {
                SimpleBlackSectionTitle("EDUCATION")
                filteredFormations.forEach { SimpleCv3EducationItem(it) }
            }

            // ATOUTS (permis + qualités)
            val atoutsItems = buildList {
                if (pi.drivingLicense.isNotBlank()) add(pi.drivingLicense)
                addAll(filteredQualities)
            }
            if (atoutsItems.isNotEmpty()) {
                SimpleBlackSectionTitle("ATOUTS")
                Ul(attrs = { style { fontSize(11.px); color(grayText); paddingLeft(15.px); margin(0.px) } }) {
                    atoutsItems.forEach { item -> Li { Text(item) } }
                }
            }

            // LANGUES
            if (filteredLanguages.isNotEmpty()) {
                SimpleBlackSectionTitle("LANGUES")
                filteredLanguages.forEach { SimpleCv3LanguageRow(it.language.name, it.level.num.toDouble()) }
            }

            // HOBBIES
            if (filteredHobbies.isNotEmpty()) {
                SimpleBlackSectionTitle("HOBBIES")
                Div(attrs = { style {
                    display(DisplayStyle.Grid)
                    property(
                        "grid-template-columns",
                        "minmax(0, 1fr) minmax(0, 1fr)"
                    )
                    property("row-gap", "8px")
                    fontSize(11.px)
                    color(Colors.Black)
                    textAlign("center")
                }}) {
                    filteredHobbies.forEach { hobby ->
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Block)
                                width(100.percent)
                                property("max-width", "100%")
                                property("min-width", "0")
                                property("white-space", "normal")
                                property("overflow-wrap", "anywhere")
                                property("word-break", "break-word")
                            }
                        }) {
                            Text(hobby)
                        }
                    }
                }
            }
        }

        // --- CORPS DROITE ---
        Div(attrs = {
            style {
                flex(1)
                property("width", "0")
                property("min-width", "0")
                property("max-width", "100%")
                property("overflow", "hidden")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                justifyContent(JustifyContent.SpaceBetween)
                padding(40.px, 30.px, 0.px, 20.px)
                boxSizing("border-box")
            }
        }) {
            // Container pour le contenu du haut (Header + Propos + Exp + ...)
            Div {
                // HEADER
                if (pi.firstName.isNotBlank() || pi.lastName.isNotBlank() || pi.bp.isNotBlank() || pi.qrCodePath.isNotBlank()) {
                    Div(attrs = { style { display(DisplayStyle.Flex)
                        width(100.percent)
                        property("max-width", "100%")
                        property("min-width", "0")
                        property("box-sizing", "border-box")
                        property("overflow", "hidden")
                        alignItems(AlignItems.FlexStart)
                        gap(12.px)
                        marginBottom(25.px)
                    } }) {
                        Div(attrs = { style { flex(1)
                            property("width", "0")
                            property("min-width", "0")
                            property("max-width", "100%")
                            property("overflow", "hidden") } }) {
                            if (pi.firstName.isNotBlank() || pi.lastName.isNotBlank()) {
                                H1(attrs = {
                                    style {
                                        width(100.percent)
                                        property("max-width", "100%")
                                        property("min-width", "0")
                                        margin(0.px)
                                        fontSize(28.px)
                                        fontWeight("bold")
                                        color(Colors.Black)
                                        lineHeight("1.2")
                                        property("white-space", "normal")
                                        property("overflow-wrap", "anywhere")
                                        property("word-break", "break-word")
                                    }
                                }) {
                                    Text("${pi.firstName} ${pi.lastName}".trim())
                                }
                            }

                            if (pi.bp.isNotBlank()) {
                                Div(attrs = {
                                    style {
                                        display(DisplayStyle.Block)
                                        width(100.percent)
                                        property("max-width", "100%")
                                        property("min-width", "0")
                                        backgroundColor(Colors.orange)
                                        color(Colors.White)
                                        fontWeight("bold")
                                        fontSize(16.px)
                                        lineHeight("1.3")
                                        padding(10.px, 20.px)
                                        marginTop(10.px)
                                        property("white-space", "normal")
                                        property("overflow-wrap", "anywhere")
                                        property("word-break", "break-word")
                                    }
                                }) {
                                    Text(pi.bp)
                                }
                            }
                        }

                        // QR Code
                        if (pi.qrCodePath.isNotBlank()) {
                            Div(attrs = { style {
                                width(65.px); height(65.px); border(1.px, LineStyle.Solid, Colors.Black)
                                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                                overflow("hidden")
                            } }) {
                                Img(src = pi.qrCodePath, attrs = { style {
                                    width(100.percent); height(100.percent); property("object-fit", "contain")
                                }})
                            }
                        }
                    }
                }

                // À PROPOS
                if (!data.profil.isNullOrBlank()) {
                    SimpleBlackSectionTitle("À PROPOS")
                    HtmlBlock(data.profil) {
                        fontSize(11.px); lineHeight("1.6"); color(grayText); textAlign("justify"); marginBottom(25.px)
                    }
                }

                // RUBRIQUES PERSONNALISÉES
                if (validPersonalizedSections.isNotEmpty()) {
                    validPersonalizedSections.forEach { section ->
                        SimpleCv3PersonalizedSection(
                            section = section,
                            grayText = grayText
                        )
                    }
                }

                // EXPÉRIENCES PROFESSIONNELLES
                if (filteredExperiences.isNotEmpty()) {
                    SimpleBlackSectionTitle("EXPÉRIENCES PROFESSIONNELLES")
                    filteredExperiences.forEach { exp ->
                        SimpleCv3TimelineItem(
                            period   = formatPeriod(exp.started, exp.ended),
                            title    = exp.post,
                            subtitle = exp.enterprise,
                            city     = exp.city,
                            bullets  = exp.description.filter { it.isNotBlank() }
                        )
                    }
                }

                // STAGES
                if (filteredStages.isNotEmpty()) {
                    SimpleBlackSectionTitle("STAGES")
                    filteredStages.forEach { stage ->
                        SimpleCv3TimelineItem(
                            period   = formatPeriod(stage.started, stage.ended),
                            title    = stage.post,
                            subtitle = stage.enterprise,
                            city     = stage.city,
                            bullets  = if (stage.description.isNotBlank()) listOf(stage.description) else emptyList()
                        )
                    }
                }

                // COURS
                if (filteredLearns.isNotEmpty()) {
                    SimpleBlackSectionTitle("COURS")
                    filteredLearns.forEach { learn ->
                        SimpleCv3TimelineItem(
                            period   = formatPeriod(learn.started, learn.ended),
                            title    = learn.label,
                            subtitle = "",
                            city     = "",
                            bullets  = if (learn.description.isNotBlank()) listOf(learn.description) else emptyList()
                        )
                    }
                }

                // ACTIVITÉS EXTRA-SCOLAIRES
                if (filteredActivities.isNotEmpty()) {
                    SimpleBlackSectionTitle("ACTIVITÉS EXTRA-SCOLAIRES")
                    filteredActivities.forEach { activity ->
                        SimpleCv3TimelineItem(
                            period   = formatPeriod(activity.started, activity.ended),
                            title    = activity.name,
                            subtitle = activity.enterprise,
                            city     = activity.city,
                            bullets  = if (activity.description.isNotBlank()) listOf(activity.description) else emptyList()
                        )
                    }
                }

                // CERTIFICATS
                if (filteredCertificates.isNotEmpty()) {
                    SimpleBlackSectionTitle("CERTIFICATS")
                    filteredCertificates.forEach { cert ->
                        SimpleCv3TimelineItem(
                            period   = extractYear(cert.date),
                            title    = cert.name,
                            subtitle = "",
                            city     = "",
                            bullets  = if (cert.description.isNotBlank()) listOf(cert.description) else emptyList()
                        )
                    }
                }

                // RÉALISATIONS
                if (filteredAchievements.isNotEmpty()) {
                    SimpleBlackSectionTitle("RÉALISATIONS")
                    SimpleCv3BulletList(filteredAchievements)
                }

                // RÉFÉRENCES
                if (filteredReferences.isNotEmpty()) {
                    SimpleBlackSectionTitle("RÉFÉRENCES")
                    filteredReferences.forEach { ref -> SimpleCv3ReferenceCard(ref) }
                }

                // SIGNATURE
                data.signature?.let { sig ->
                    if (sig.city.isNotBlank() || sig.date.isNotBlank() || sig.image.isNotBlank()) {
                        SimpleCv3SignatureBlock(sig)
                    }
                }
            }

            // --- FOOTER NOIR (Compétences & Logiciels) ---
            if (filteredCompetences.isNotEmpty() || filteredSoftware.isNotEmpty()) {
                Div(attrs = {
                    style {
                        backgroundColor(Colors.Black)
                        color(Colors.White)
                        display(DisplayStyle.Flex)
                        gap(30.px)
                        property("min-width", "0")
                        property("max-width", "100%")
                        property("box-sizing", "border-box")
                        padding(20.px, 30.px)
                        margin(20.px, (-30).px, 0.px, (-20).px)
                        minHeight(200.px)
                    }
                }) {
                    // COMPÉTENCES
                    if (filteredCompetences.isNotEmpty()) {
                        Div(attrs = { style {
                            property(
                                "width",
                                if (filteredSoftware.isNotEmpty()) {
                                    "calc(50% - 15px)"
                                } else {
                                    "100%"
                                }
                            )
                            property("min-width", "0")
                            property("max-width", "100%")
                            property("overflow", "hidden")
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                        } }) {
                            H3(attrs = { style { margin(0.px, 0.px, 15.px, 0.px); fontSize(13.px); property("text-transform", "uppercase") } }) { Text("COMPÉTENCES") }
                            filteredCompetences.forEach { comp ->
                                SimpleCv3ProgressBar(comp.name, comp.level.num.toDouble())
                            }
                        }
                    }

                    // LOGICIELS
                    if (filteredSoftware.isNotEmpty()) {
                        Div(attrs = { style {
                            width(if (filteredCompetences.isNotEmpty()) 50.percent else 100.percent)
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                        } }) {
                            H3(attrs = { style { margin(0.px, 0.px, 15.px, 0.px); fontSize(13.px); property("text-transform", "uppercase") } }) { Text("LOGICIELS") }
                            filteredSoftware.forEach { sw ->
                                SimpleCv3ProgressBar(sw.name, (sw.stars.coerceIn(0, 5)) / 5.0)
                            }
                        }
                    }
                }
            }
        }
    }
}

// --- HELPERS REUTILISABLES ---

@Composable
fun SimpleBlackSectionTitle(title: String) {
    H2(attrs = {
        style {
            color(Colors.Black)
            fontSize(14.px)
            fontWeight("bold")
            property("text-transform", "uppercase")
            marginBottom(10.px)
            marginTop(20.px)
        }
    }) { Text(title) }
}

@Composable
fun SimpleCv3ContactRow(value: String) {
    if (value.isBlank()) return

    Div(attrs = {
        style {
            display(DisplayStyle.Block)
            width(100.percent)
            property("max-width", "100%")
            property("min-width", "0")
            fontSize(11.px)
            color(Colors.Black)
            marginBottom(5.px)
            textAlign("center")
            lineHeight("1.4")
            property("white-space", "normal")
            property("overflow-wrap", "anywhere")
            property("word-break", "break-word")
        }
    }) {
        Text(value)
    }
}

@Composable
fun SimpleCv3ContactLinkRow(url: String) {
    if (url.isBlank()) return

    Div(attrs = {
        style {
            width(100.percent)
            property("max-width", "100%")
            property("min-width", "0")
            fontSize(11.px)
            marginBottom(5.px)
            textAlign("center")
            property("overflow-wrap", "anywhere")
            property("word-break", "break-word")
        }
    }) {
        A(
            href = url,
            attrs = {
                attr("target", "_blank")
                attr("rel", "noopener noreferrer")

                style {
                    color(Colors.Black)
                    property("max-width", "100%")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                    property("text-decoration", "underline")
                }
            }
        ) {
            Text(url)
        }
    }
}

@Composable
fun SimpleCv3DetailRow(
    label: String,
    value: String
) {
    if (value.isBlank()) return

    Div(attrs = {
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "70px minmax(0, 1fr)")
            width(100.percent)
            property("max-width", "100%")
            property("min-width", "0")
            fontSize(11.px)
            color(Colors.Black)
            marginBottom(5.px)
        }
    }) {
        Span(attrs = {
            style {
                property("min-width", "0")
                fontWeight("bold")
            }
        }) {
            Text(label)
        }

        Span(attrs = {
            style {
                property("min-width", "0")
                property("max-width", "100%")
                property("white-space", "normal")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
            }
        }) {
            Text(value)
        }
    }
}

@Composable
fun SimpleCv3EducationItem(
    formation: io.horion.cv.api.datasource.cv.Formation
) {
    Div(attrs = {
        style {
            width(100.percent)
            property("max-width", "100%")
            property("min-width", "0")
            marginBottom(10.px)
            property("overflow", "hidden")
        }
    }) {
        if (formation.label.isNotBlank()) {
            Div(attrs = {
                style {
                    width(100.percent)
                    property("max-width", "100%")
                    property("min-width", "0")
                    fontWeight("bold")
                    fontSize(11.px)
                    lineHeight("1.3")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(formation.label)
            }
        }

        if (formation.school.isNotBlank()) {
            Div(attrs = {
                style {
                    width(100.percent)
                    property("max-width", "100%")
                    property("min-width", "0")
                    fontSize(11.px)
                    color(Color("#777"))
                    lineHeight("1.3")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(formation.school)
            }
        }

        if (!formation.fieldOfStudy.isNullOrBlank()) {
            Div(attrs = {
                style {
                    width(100.percent)
                    property("max-width", "100%")
                    property("min-width", "0")
                    fontSize(11.px)
                    color(Color("#cc6633"))
                    fontStyle("italic")
                    lineHeight("1.3")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(formation.fieldOfStudy)
            }
        }

        val period = formatPeriod(
            formation.started,
            formation.ended
        )

        if (period.isNotBlank()) {
            Div(attrs = {
                style {
                    width(100.percent)
                    property("max-width", "100%")
                    fontSize(10.px)
                    color(Color("#777"))
                    marginTop(2.px)
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(period)
            }
        }

        if (formation.description.isNotBlank()) {
            Div(attrs = {
                style {
                    width(100.percent)
                    property("max-width", "100%")
                    property("min-width", "0")
                    fontSize(10.px)
                    color(Color("#555"))
                    marginTop(3.px)
                    lineHeight("1.4")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                    property("overflow", "hidden")
                }
            }) {
                HtmlInline(formation.description)
            }
        }
    }
}

@Composable
fun SimpleCv3LanguageRow(name: String, level: Double) {
    Div(attrs = { style { display(DisplayStyle.Grid); gridTemplateColumns("1fr 100px"); alignItems(AlignItems.Center); marginBottom(8.px) } }) {
        Span(attrs = { style { fontSize(11.px); property("text-transform", "uppercase") } }) { Text(name) }
        val label = when {
            level >= 0.9 -> "EXCELLENT"
            level >= 0.7 -> "GOOD"
            level >= 0.5 -> "AVERAGE"
            else -> "BEGINNER"
        }
        Div(attrs = { style { backgroundColor(Colors.Black); color(Colors.White); fontSize(9.px); padding(3.px); textAlign("center"); property("text-transform", "uppercase") } }) { Text(label) }
    }
}

@Composable
fun SimpleCv3TimelineItem(
    period: String,
    title: String,
    subtitle: String,
    city: String,
    bullets: List<String>
) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            width(100.percent)
            property("max-width", "100%")
            property("min-width", "0")
            gap(15.px)
            marginBottom(20.px)
            position(Position.Relative)
            property("box-sizing", "border-box")
        }
    }) {
        // Période
        if (period.isNotBlank()) {
            Div(attrs = {
                style {
                    minWidth(40.px)
                    property("max-width", "55px")
                    property("flex-shrink", "0")
                    backgroundColor(Colors.Black)
                    color(Colors.White)
                    fontSize(10.px)
                    fontWeight("bold")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    property("writing-mode", "vertical-rl")
                    property("transform", "rotate(180deg)")
                    padding(8.px, 0.px)
                    property("box-sizing", "border-box")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(period)
            }
        }

        // Contenu texte
        Div(attrs = {
            style {
                flex(1)
                property("width", "0")
                property("min-width", "0")
                property("max-width", "100%")
                property("overflow", "hidden")
                paddingBottom(5.px)
                property("box-sizing", "border-box")
            }
        }) {
            // Ligne titre + ville
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    width(100.percent)
                    property("max-width", "100%")
                    property("min-width", "0")
                    alignItems(AlignItems.FlexStart)
                    gap(8.px)
                    property("box-sizing", "border-box")
                }
            }) {
                if (title.isNotBlank()) {
                    Div(attrs = {
                        style {
                            flex(1)
                            property("width", "0")
                            property("min-width", "0")
                            property("max-width", "100%")
                            fontWeight("bold")
                            fontSize(12.px)
                            color(Colors.Black)
                            lineHeight("1.3")
                            property("text-transform", "uppercase")
                            property("white-space", "normal")
                            property("overflow-wrap", "anywhere")
                            property("word-break", "break-word")
                        }
                    }) {
                        Text(title)
                    }
                }

                if (city.isNotBlank()) {
                    Div(attrs = {
                        style {
                            property("flex-shrink", "0")
                            property("width", "auto")
                            property("max-width", "40%")
                            fontSize(11.px)
                            color(Colors.orange)
                            lineHeight("1.3")
                            textAlign("right")
                            property("white-space", "normal")
                            property("overflow-wrap", "anywhere")
                            property("word-break", "break-word")
                        }
                    }) {
                        Text(city)
                    }
                }
            }

            // Entreprise ou sous-titre
            if (subtitle.isNotBlank()) {
                Div(attrs = {
                    style {
                        width(100.percent)
                        property("max-width", "100%")
                        property("min-width", "0")
                        fontSize(11.px)
                        color(Color("#888"))
                        marginTop(2.px)
                        lineHeight("1.3")
                        property("white-space", "normal")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                    }
                }) {
                    Text(subtitle)
                }
            }

            // Descriptions
            if (bullets.isNotEmpty()) {
                bullets.forEach { item ->
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            width(100.percent)
                            property("max-width", "100%")
                            property("min-width", "0")
                            gap(6.px)
                            fontSize(11.px)
                            color(Colors.Black)
                            marginTop(4.px)
                            lineHeight("1.4")
                            property("box-sizing", "border-box")
                        }
                    }) {
                        Span(attrs = {
                            style {
                                property("flex-shrink", "0")
                                property("line-height", "1.4")
                            }
                        }) {
                            Text("•")
                        }

                        Div(attrs = {
                            style {
                                flex(1)
                                property("width", "0")
                                property("min-width", "0")
                                property("max-width", "100%")
                                property("white-space", "normal")
                                property("overflow-wrap", "anywhere")
                                property("word-break", "break-word")
                                property("overflow", "hidden")
                            }
                        }) {
                            HtmlInline(item)
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun SimpleCv3BulletList(items: List<String>) {
    Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(4.px); marginBottom(15.px) } }) {
        items.forEach { item ->
            Div(attrs = { style { display(DisplayStyle.Flex); gap(6.px); fontSize(11.px); color(Colors.Black) } }) {
                Span { Text("•") }
                HtmlInline(item)
            }
        }
    }
}

@Composable
fun SimpleCv3ReferenceCard(ref: io.horion.cv.api.datasource.cv.Reference) {
    Div(
        attrs = {
            style {
                marginBottom(10.px);
                paddingBottom(8.px);
                property("border-bottom", "1px solid #eeeeee")
                property("width", "100%")
                property("max-width", "100%")
                property("min-width", "0")
                property("white-space", "normal")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
            } }) {
        Div(
            attrs = {
                style {
                    fontWeight("bold");
                    fontSize(11.px);
                    color(Colors.Black)
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                } }) { Text(ref.name) }
        val subtitleParts = listOfNotNull(ref.enterprise.takeIf { it.isNotBlank() }, ref.city.takeIf { it.isNotBlank() })
        if (subtitleParts.isNotEmpty()) {
            Div(
                attrs = {
                    style {
                        fontSize(10.px);
                        color(Colors.orange);
                        marginTop(2.px)
                        property("width", "100%")
                        property("max-width", "100%")
                        property("min-width", "0")
                        property("white-space", "normal")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                    } }) { Text(subtitleParts.joinToString(" · ")) }
        }
        val contactParts = listOfNotNull(ref.phone.takeIf { it.isNotBlank() }, ref.email.takeIf { it.isNotBlank() })
        if (contactParts.isNotEmpty()) {
            Div(
                attrs = {
                    style {
                        fontSize(10.px);
                        color(Colors.Black);
                        marginTop(3.px)
                        property("width", "100%")
                        property("max-width", "100%")
                        property("min-width", "0")
                        property("white-space", "normal")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                    } }) { Text(contactParts.joinToString("   •   ")) }
        }
    }
}

@Composable
fun SimpleCv3SignatureBlock(
    sig: io.horion.cv.api.datasource.common.Signature
) {
    Div(attrs = {
        style {
            marginTop(20.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.FlexEnd)
            width(100.percent)
            property("max-width", "100%")
            property("min-width", "0")
            property("overflow", "hidden")
        }
    }) {
        Div(attrs = {
            style {
                width(100.percent)
                property("max-width", "100%")
                property("min-width", "0")
                textAlign("right")
                property("white-space", "normal")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
            }
        }) {
            val line = listOfNotNull(
                sig.city.takeIf { it.isNotBlank() },
                sig.date.takeIf { it.isNotBlank() }
            )

            if (line.isNotEmpty()) {
                Div(attrs = {
                    style {
                        fontSize(10.px)
                        color(Color("#777"))
                        lineHeight("1.3")
                        property("white-space", "normal")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                    }
                }) {
                    Text(line.joinToString(", "))
                }
            }

            if (sig.image.isNotBlank()) {
                Img(
                    src = sig.image,
                    attrs = {
                        style {
                            property("display", "inline-block")
                            property("max-width", "100%")
                            height(50.px)
                            marginTop(4.px)
                            property("object-fit", "contain")
                        }
                    }
                )
            }
        }
    }
}

@Composable
fun SimpleCv3ProgressBar(label: String, level: Double) {
    Div(attrs = { style { marginBottom(12.px) } }) {
        Div(attrs = { style { fontSize(11.px); marginBottom(4.px); property("overflow-wrap", "break-word") } }) { Text(label) }
        Div(attrs = {
            style {
                height(6.px)
                backgroundColor(Color("#444"))
                marginTop(4.px)
            }
        }) {
            Div(attrs = {
                style {
                    height(100.percent)
                    backgroundColor(Colors.orange)
                    width((level * 100).percent)
                }
            }) {}
        }
    }
}

@Composable
private fun SimpleCv3PersonalizedSection(
    section: io.horion.cv.api.datasource.cv.Section,
    grayText: CSSColorValue
) {
    val title = section.title
        .ifBlank { "RUBRIQUE PERSONNALISÉE" }

    SimpleBlackSectionTitle(title)

    Div(attrs = {
        style {
            property("display", "block")
            width(100.percent)
            property("max-width", "100%")
            property("min-width", "0")
            property("box-sizing", "border-box")
            property("margin-bottom", "18px")
            property("overflow", "hidden")
            property("line-height", "1.5")
        }
    }) {
        if (section.resume.isNotBlank()) {
            Div(attrs = {
                style {
                    width(100.percent)
                    property("max-width", "100%")
                    property("min-width", "0")
                    fontSize(11.px)
                    fontWeight("bold")
                    color(Colors.Black)
                    lineHeight("1.4")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(section.resume)
            }
        }

        if (
            section.started.isNotBlank() ||
            section.ended.isNotBlank()
        ) {
            Div(attrs = {
                style {
                    width(100.percent)
                    property("max-width", "100%")
                    property("min-width", "0")
                    fontSize(10.px)
                    color(Color("#777777"))
                    lineHeight("1.3")
                    marginTop(3.px)
                    marginBottom(5.px)
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(
                    listOf(
                        section.started,
                        section.ended
                    )
                        .filter { it.isNotBlank() }
                        .joinToString(" — ")
                )
            }
        }

        if (section.description.isNotBlank()) {
            Div(attrs = {
                style {
                    width(100.percent)
                    property("max-width", "100%")
                    property("min-width", "0")
                    fontSize(11.px)
                    color(grayText)
                    lineHeight("1.5")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                    property("overflow", "hidden")
                }
            }) {
                HtmlBlock(section.description) {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    fontSize(11.px)
                    color(grayText)
                    lineHeight("1.5")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }
        }
    }
}